import React, { useEffect, useState, useRef } from 'react'
import { Card, CardBody, CardHeader } from '../../../../_metronic/_partials/controls'
import { sendCampaign, getBrevoStats } from '../../../../api/brevo'
import { uploadSingleFile } from '../../../../api/files'
import { SERVER_URL } from '../../../../api'
import { Button, TextField, Typography, Box, CircularProgress, Chip, Paper } from '@material-ui/core'
import { alertError, alertSuccess } from '../../../../utils/logger'
import Editor from '../../../components/editor/Editor'
import SendIcon from '@material-ui/icons/Send'
import PeopleIcon from '@material-ui/icons/People'
import ImageIcon from '@material-ui/icons/Image'

export default function CampanasPromocionalesPage() {
  const imageInputRef = useRef(null)
  const [loading, setLoading] = useState(false)
  const [loadingStats, setLoadingStats] = useState(true)
  const [stats, setStats] = useState(null)
  const [formData, setFormData] = useState({
    subject: '',
    htmlContent: '',
    textContent: '',
    senderName: 'Tu Reloj Ya',
    senderEmail: 'noreply@turelojya.com'
  })

  useEffect(() => {
    // Cargar estadísticas al montar el componente
    loadStats()
  }, [])

  const loadStats = async () => {
    setLoadingStats(true)
    try {
      const res = await getBrevoStats()
      if (res.status === 200) {
        setStats(res.data)
      }
    } catch (error) {
      console.error('Error loading stats:', error)
      // No mostrar error si Brevo no está configurado
    } finally {
      setLoadingStats(false)
    }
  }

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const [uploadingImage, setUploadingImage] = useState(false)

  const handleAddImage = async (e) => {
    const file = e.target.files?.[0]
    if (!file || !file.type.startsWith('image/')) {
      if (file) alertError({ customMessage: 'Solo se permiten archivos de imagen (jpg, png, gif, etc.).' })
      return
    }
    setUploadingImage(true)
    try {
      const res = await uploadSingleFile(file, 'campaigns')
      const path = res?.data?.fileURL || res?.data?.url || res?.data?.path
      if (!path) {
        throw new Error('No se devolvió la URL de la imagen')
      }
      const baseUrl = (SERVER_URL || '').replace(/\/$/, '')
      const imageUrl = path.startsWith('http') ? path : `${baseUrl}${path.startsWith('/') ? path : '/' + path}`
      setFormData(prev => ({
        ...prev,
        htmlContent: (prev.htmlContent || '') + '<p><img src="' + imageUrl + '" alt="Imagen" style="max-width:100%;height:auto;max-height:400px;" /></p>'
      }))
      alertSuccess({ customMessage: 'Imagen subida y añadida al contenido. Se verá en el correo.' })
    } catch (err) {
      console.error('Error subiendo imagen:', err)
      alertError({ customMessage: 'No se pudo subir la imagen. Comprueba que estés logueado y que el servidor permita subir archivos.' })
    } finally {
      setUploadingImage(false)
      e.target.value = ''
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!formData.subject || !formData.htmlContent) {
      alertError({ customMessage: 'El asunto y el contenido HTML son obligatorios.' })
      return
    }

    setLoading(true)
    try {
      const res = await sendCampaign(formData)
      if (res.status === 200) {
        alertSuccess({
          title: '¡Campaña enviada!',
          customMessage: `La campaña se ha enviado correctamente a ${res.data.recipientsCount} destinatarios.`
        })
        // Limpiar formulario
        setFormData({
          subject: '',
          htmlContent: '',
          textContent: '',
          senderName: 'Tu Reloj Ya',
          senderEmail: 'noreply@turelojya.com'
        })
        // Recargar estadísticas
        loadStats()
      }
    } catch (error) {
      alertError({ 
        error: error, 
        customMessage: 'No se pudo enviar la campaña. Verifica que Brevo esté configurado correctamente.' 
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <Card>
        <CardHeader title='Campañas Promocionales'>
          <Box display="flex" alignItems="center" gap={2} mt={2}>
            {loadingStats ? (
              <CircularProgress size={20} />
            ) : stats ? (
              <>
                <PeopleIcon style={{ color: '#6b7280' }} />
                <Typography variant="body2" color="textSecondary">
                  <strong>{stats.uniqueSubscribers || 0}</strong> suscriptores en la lista "{stats.name || 'Promociones'}"
                </Typography>
              </>
            ) : (
              <Paper style={{ padding: '12px', backgroundColor: '#fff3cd', border: '1px solid #ffc107', width: '100%' }}>
                <Typography variant="body2" style={{ color: '#856404' }}>
                  ⚠️ Brevo no está configurado o no se pudo obtener las estadísticas. Verifica las variables de entorno.
                </Typography>
              </Paper>
            )}
          </Box>
        </CardHeader>
        <CardBody>
          <form onSubmit={handleSubmit}>
            <Box mb={3}>
              <TextField
                fullWidth
                label="Asunto del correo"
                variant="outlined"
                value={formData.subject}
                onChange={(e) => handleInputChange('subject', e.target.value)}
                required
                helperText="Este será el asunto del correo promocional"
              />
            </Box>

            <Box mb={3}>
              <Typography variant="subtitle2" gutterBottom>
                Contenido HTML <span style={{ color: 'red' }}>*</span>
              </Typography>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <input
                  ref={imageInputRef}
                  type="file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={handleAddImage}
                />
                <Button
                  type="button"
                  variant="outlined"
                  size="small"
                  startIcon={uploadingImage ? <CircularProgress size={18} /> : <ImageIcon />}
                  onClick={() => imageInputRef.current?.click()}
                  disabled={uploadingImage}
                >
                  {uploadingImage ? 'Subiendo...' : 'Añadir imagen'}
                </Button>
                <Typography variant="caption" color="textSecondary">
                  La imagen se sube al servidor y se inserta con su URL para que se vea correctamente en el correo.
                </Typography>
              </Box>
              <Editor
                body={formData.htmlContent}
                setBody={(content) => handleInputChange('htmlContent', content)}
                placeholder="Escribe el contenido HTML del correo promocional..."
                lang="es"
              />
            </Box>

            <Box mb={3}>
              <TextField
                fullWidth
                label="Contenido de texto plano (opcional)"
                variant="outlined"
                multiline
                rows={4}
                value={formData.textContent}
                onChange={(e) => handleInputChange('textContent', e.target.value)}
                helperText="Versión de texto plano del correo (para clientes que no soportan HTML)"
              />
            </Box>

            <Box display="flex" gap={2} mb={3}>
              <TextField
                fullWidth
                label="Nombre del remitente"
                variant="outlined"
                value={formData.senderName}
                onChange={(e) => handleInputChange('senderName', e.target.value)}
                required
              />
              <TextField
                fullWidth
                label="Email del remitente"
                variant="outlined"
                type="email"
                value={formData.senderEmail}
                onChange={(e) => handleInputChange('senderEmail', e.target.value)}
                required
              />
            </Box>

            <Box display="flex" justifyContent="flex-end" gap={2}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                startIcon={loading ? <CircularProgress size={20} /> : <SendIcon />}
                disabled={loading || !formData.subject || !formData.htmlContent}
                size="large"
              >
                {loading ? 'Enviando...' : 'Enviar Campaña'}
              </Button>
            </Box>
          </form>

          {stats && stats.uniqueSubscribers === 0 && (
            <Paper style={{ padding: '16px', backgroundColor: '#d1ecf1', border: '1px solid #bee5eb', marginTop: '20px' }}>
              <Typography variant="body2" style={{ color: '#0c5460', marginBottom: '8px' }}>
                ℹ️ No hay suscriptores en la lista de promociones. Los usuarios se agregarán automáticamente cuando:
              </Typography>
              <ul style={{ marginTop: '8px', marginBottom: 0, paddingLeft: '20px', color: '#0c5460' }}>
                <li>Se registren con "Recibir ofertas y promociones" activado y verifiquen su email</li>
                <li>Activen "Recibir ofertas y promociones" desde sus preferencias en el área privada</li>
              </ul>
            </Paper>
          )}
        </CardBody>
      </Card>
    </>
  )
}

